// pages/Services/styles.ts
import { SxProps, Theme } from '@mui/material/styles';
import { colors } from '../../styles/colors';
import { breakpoints } from '../../styles/breakpoints';

/**
 * Services page styles.
 *
 * Two things were cleaned up here.
 *
 * 1. Roughly half of this file was dead. A second, near-identical style
 *    object called `enhancedSidebarStyles` was declared inline inside
 *    Services.tsx and shadowed the shell rules below (`container`,
 *    `headerSection`, `contentArea`, `contentPanel`, `tabsContainer`,
 *    `searchContainer`, `searchField`, `tabs`, `tab`, `title`,
 *    `actionButton`, `servicesGrid`), so editing them here changed nothing on
 *    screen. A third export, `sidebarIntegrationStyles`, was never imported
 *    anywhere at all. The shell rules now live in `servicesShellStyles` below
 *    as the single source of truth, and the dead export is gone.
 *
 * 2. The colours were hardcoded and off-brand: `#1976d2` (MUI's default blue)
 *    instead of the app's `colors.primary` #0898FF, and `#ff6b35` instead of
 *    `colors.secondary` #FF6F08. Both are close enough to look like a
 *    rendering bug rather than a deliberate accent.
 *
 * MUI `sx` takes breakpoints as objects keyed by theme breakpoint name, so
 * these use `@media` strings built from the shared tokens to stay in step with
 * the JSS pages.
 */

const down = {
  sm: `@media (max-width: ${breakpoints.sm}px)`,
  md: `@media (max-width: ${breakpoints.md}px)`,
};

/* --------------------------------------------------------------------------
   Page shell — previously inlined in Services.tsx
   -------------------------------------------------------------------------- */

export const servicesShellStyles: Record<string, SxProps<Theme>> = {
  /** Fills the area the Sidebar hands over, and scrolls internally. */
  mainContainer: {
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.bgcolor,
    /*
     * `height: 100%` + `overflow: hidden` used to sit here. The parent's
     * height is `auto`, so the percentage resolved to `auto` too and the
     * `hidden` was guarding nothing -- except that it would have started
     * clipping the page the moment the parent ever gained a fixed height.
     * <main> in the shell is already the scroll container.
     */
    minHeight: '100%',
  },

  headerSection: {
    backgroundColor: colors.WHITE,
    padding: '24px 32px',
    borderBottom: `1px solid ${colors.default}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    /* The title and the action button sat on one non-wrapping line, so the
       button was pushed off the right edge on a tablet. */
    flexWrap: 'wrap',
    gap: '16px',
    flexShrink: 0,
    [down.md]: {
      padding: '16px 20px',
    },
    [down.sm]: {
      padding: '12px 16px',
      alignItems: 'stretch',
    },
  },

  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  },

  contentArea: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    padding: '24px 32px',
    overflow: 'auto',
    backgroundColor: colors.bgcolor,
    [down.md]: {
      padding: '16px 20px',
    },
    [down.sm]: {
      padding: '12px',
    },
  },

  contentPanel: {
    backgroundColor: colors.WHITE,
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    minWidth: 0,
    /*
     * Was `minHeight: calc(100vh - 200px)`. The 200px was a guess at the
     * header height that ignored the 56px mobile top bar entirely, so on a
     * phone the panel was always taller than the space available and left a
     * dead scroll region below the content. The panel now simply fits its
     * content; `contentArea` above provides the scrolling.
     */
    [down.sm]: {
      padding: '16px',
    },
  },

  tabsContainer: {
    marginBottom: '24px',
    borderBottom: `1px solid ${colors.default}`,
    [down.sm]: {
      marginBottom: '16px',
    },
  },

  tabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      minHeight: 48,
      fontSize: '14px',
      color: colors.greydark,
      '&.Mui-selected': {
        color: colors.primary,
        fontWeight: 600,
      },
    },
    '& .MuiTabs-indicator': {
      height: 3,
      backgroundColor: colors.primary,
    },
  },

  searchContainer: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'flex-start',
    [down.sm]: {
      marginBottom: '16px',
    },
  },

  /* A flat `width: 300px` could not shrink and overflowed the panel on a
     phone. */
  searchField: {
    width: '300px',
    maxWidth: '100%',
    [down.sm]: {
      width: '100%',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors.bgcolor,
      height: '40px',
      borderRadius: '4px',
      '& fieldset': {
        borderColor: colors.default,
      },
      '&:hover fieldset': {
        borderColor: colors.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
        borderWidth: '1px',
      },
    },
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '8px 12px',
    },
  },

  /** Page title — bold, uppercase. */
  title: {
    /* Replaces a flat 24px that wrapped awkwardly on narrow screens. */
    fontSize: 'clamp(18px, 1.6vw + 12px, 24px)',
    fontWeight: 'bold',
    color: colors.darkblack,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    margin: 0,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },

  /** Primary action button — was `#ff6b35`, an off-brand orange. */
  actionButton: {
    backgroundColor: colors.secondary,
    color: colors.WHITE,
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 'bold',
    padding: '8px 16px',
    /* 44px keeps this a comfortable tap target (WCAG 2.5.5). */
    minHeight: 44,
    textTransform: 'uppercase',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.2s ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: colors.orangeDark,
      },
    },
    [down.sm]: {
      width: '100%',
    },
  },
};

/* --------------------------------------------------------------------------
   Content
   -------------------------------------------------------------------------- */

export const serviceManagementPageStyles: Record<string, SxProps<Theme>> = {
  contentContainer: {
    width: '100%',
    minWidth: 0,
    overflow: 'visible',
  },

  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    /* Was a fixed 300px, which overflowed a landscape phone. */
    minHeight: '300px',
    padding: '24px',
    width: '100%',
  },

  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 24px',
    width: '100%',
  },

  errorAlert: {
    maxWidth: 500,
    width: '100%',
    borderRadius: '8px',
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 24px',
    textAlign: 'center',
    width: '100%',
    [down.sm]: {
      padding: '40px 16px',
    },
  },

  emptyIcon: {
    fontSize: 48,
    color: colors.greydark,
    marginBottom: '16px',
  },

  emptyTitle: {
    color: colors.BLACK,
    marginBottom: '8px',
    fontSize: '18px',
    fontWeight: 500,
  },

  /* ── Categories ── */

  categoriesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    minWidth: 0,
  },

  categoryCard: {
    border: `1px solid ${colors.default}`,
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: colors.WHITE,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderColor: colors.primary,
      },
    },
  },

  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    /* Name + action icons ran into each other on a phone. */
    flexWrap: 'wrap',
    gap: '8px',
    padding: '16px 20px',
    backgroundColor: colors.WHITE,
    borderBottom: '1px solid transparent',
    [down.sm]: {
      padding: '12px 14px',
    },
  },

  categoryInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },

  expandButton: {
    color: colors.greydark,
    flexShrink: 0,
    transition: 'color 0.2s ease, background-color 0.2s ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.primary,
        backgroundColor: 'rgba(8, 152, 255, 0.06)',
      },
    },
  },

  categoryName: {
    fontWeight: 500,
    color: colors.BLACK,
    fontSize: '16px',
    marginBottom: '2px',
    minWidth: 0,
    overflowWrap: 'anywhere',
  },

  categoryActions: {
    display: 'flex',
    gap: '4px',
    flexShrink: 0,
  },

  /*
   * The edit/delete/manage buttons all scaled up on hover. On a touch screen
   * the hover state sticks after a tap, leaving a button permanently enlarged,
   * so the scale is now desktop-only.
   */
  editButton: {
    color: colors.primary,
    transition: 'color 0.2s ease, background-color 0.2s ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.darkBlue,
        backgroundColor: 'rgba(8, 152, 255, 0.06)',
        transform: 'scale(1.05)',
      },
    },
  },

  deleteButton: {
    color: colors.red,
    transition: 'color 0.2s ease, background-color 0.2s ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.tertiary,
        backgroundColor: 'rgba(222, 68, 68, 0.06)',
        transform: 'scale(1.05)',
      },
    },
  },

  manageButton: {
    color: colors.success,
    transition: 'color 0.2s ease, background-color 0.2s ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        color: colors.greenDark,
        backgroundColor: 'rgba(57, 184, 46, 0.08)',
        transform: 'scale(1.05)',
      },
    },
  },

  categoryServices: {
    borderTop: `1px solid ${colors.default}`,
    padding: '16px 20px',
    backgroundColor: '#fafafa',
    [down.sm]: {
      padding: '12px 14px',
    },
  },

  serviceInCategory: {
    p: 1.5,
    backgroundColor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    minWidth: 0,
    transition: 'border-color 0.2s ease',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        borderColor: 'primary.main',
      },
    },
  },

  serviceName: {
    fontWeight: 500,
    color: 'text.primary',
    overflowWrap: 'anywhere',
  },

  /* ── Services ── */

  serviceCard: {
    p: 3,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    height: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'background.paper',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transform: 'translateY(-2px)',
      },
    },
    [down.sm]: {
      p: 2,
    },
  },

  serviceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 2,
    minWidth: 0,
  },

  serviceInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    minWidth: 0, // allows text truncation
  },

  serviceTitle: {
    fontWeight: 500,
    color: 'text.primary',
    lineHeight: 1.2,
    fontSize: '1.1rem',
    overflowWrap: 'anywhere',
  },

  serviceCode: {
    backgroundColor: 'grey.100',
    color: 'text.secondary',
    alignSelf: 'flex-start',
    maxWidth: '100%',
    fontSize: '0.75rem',
    fontWeight: 500,
  },

  serviceCategories: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    minWidth: 0,
  },

  categoriesLabel: {
    fontWeight: 500,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: 'text.secondary',
    letterSpacing: '0.5px',
  },

  categoryChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
    minWidth: 0,
  },

  categoryChip: {
    borderColor: colors.primary,
    color: colors.primary,
    fontSize: '0.7rem',
    height: 20,
    maxWidth: '100%',
    '& .MuiChip-label': {
      px: 1,
    },
  },

  serviceActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flexShrink: 0,
  },
};
